/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars */
import React, { FC, useState, useMemo } from 'react';
import { Table, Badge, Modal, Typography } from '@crisellevaldez/speculo-ui';
import { PhoneIncoming, PhoneOutgoing, User, Voicemail, XCircle } from 'lucide-react';
import { ReactNode } from 'react';
import { formatDate } from '../../../utils/dateTime';
import { formatPhoneNumber } from '../../../utils/formatters';
import { formatCallDuration } from '../../../utils/duration';
import { getAnsweredByStyle, getFlowTypeStyle } from '../../../utils/badgeStyles';
import { OutcomeBadge } from '../../common/OutcomeBadge';
import { useContactCallsGQL } from '../../../hooks/graphql/contacts/useContactCallsGQL';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { CallDetailsPanel } from './CallDetailsPanel';
import { TablePagination } from '../../common/TablePagination';
import { OffsetPagination } from '../../common/OffsetPagination';
import { TableCard } from '../../common/TableCard';
import { useDateRangeWithDefault } from '../../../hooks/useDateRangeWithDefault';
import type { GQLCallItem } from '../../../hooks/graphql/contacts/useContact';
import { EditCallAnalysisModal } from './EditCallAnalysisModal';

interface Column {
  header: ReactNode;
  key: string;
  cell?: (item: any) => ReactNode;
  width?: string;
  isPinned?: boolean;
  pinPosition?: string;
  sortable?: boolean;
  isCentered?: boolean;
}

interface ServerPagination {
  hasNext: boolean;
  onNextPage: () => void;
  isLoading: boolean;
  /** Offset-pagination fields — when present, render OffsetPagination
   *  (prev / numbered / next) instead of the See More button. */
  currentPage?: number;
  total?: number;
  pageSize?: number;
  onPrevPage?: () => void;
  onPageChange?: (page: number) => void;
}

interface ContactCallHistoryProps {
  contactId: string;
  dateRange?: { from: Date | null; to: Date | null };
  contactFirstName?: string;
  /** When provided, skips the Firestore query and uses GQL data instead */
  gqlCalls?: GQLCallItem[];
  /** When provided, overrides internal client-side pagination with server-side */
  serverPagination?: ServerPagination;
}

/** Adapts a GQLCallItem to the shape expected by table columns */
function adaptGQLCall(call: GQLCallItem) {
  const timeline = (() => {
    try {
      const parsed = call.callAnalysisTimeline ? JSON.parse(call.callAnalysisTimeline) : undefined;
      return Array.isArray(parsed) ? parsed : undefined;
    } catch {
      return undefined;
    }
  })();
  const answers = (() => {
    try {
      return call.callAnalysisAnswers ? JSON.parse(call.callAnalysisAnswers) : undefined;
    } catch {
      return undefined;
    }
  })();

  return {
    id: call.conversationId ? `${call.callId}::${call.conversationId}` : call.callId,
    call_id: call.callId,
    inbound: call.inbound ?? call.direction === 'inbound',
    to_number: call.toNumber,
    from_number: call.fromNumber,
    callTimestamp: call.initiatedAt ? new Date(call.initiatedAt) : null,
    call_length: call.durationSeconds != null ? call.durationSeconds / 60 : null,
    duration: call.durationSeconds ?? 0,
    answered_by: call.answeredBy ?? null,
    call_analysis: {
      outcome: call.outcome || null,
      summary: call.summary || null,
      timeline,
      answers,
    },
    concatenated_transcript: call.transcript ?? undefined,
    flowExecution: call.flowName
      ? { id: '', call_id: call.callId, flow_name: call.flowName, flow_type: call.flowType ?? '' }
      : null,
    sentiment: call.sentiment,
    recordingUrl: call.recordingUrl,
    transfer: call.transfer ?? null,
  };
}

export const ContactCallHistory: FC<ContactCallHistoryProps> = ({
  contactId,
  dateRange: propDateRange,
  contactFirstName,
  gqlCalls,
  serverPagination,
}) => {
  // Use the custom hook for date range with default fallback if no prop is provided
  const { dateRange } = useDateRangeWithDefault(propDateRange);

  // GQL path — skipped when gqlCalls is provided externally
  const {
    calls: internalCalls,
    hasNext: internalHasNext,
    loading: internalLoading,
    error: queryError,
    handleNextPage: internalHandleNextPage,
  } = useContactCallsGQL(gqlCalls ? '' : contactId, propDateRange || dateRange);

  // Adapt GQL or external gqlCalls data into a unified shape for table columns
  const allCalls = useMemo(() => {
    if (gqlCalls) {
      return gqlCalls.map(adaptGQLCall);
    }
    return internalCalls.map(adaptGQLCall);
  }, [gqlCalls, internalCalls]);

  const loading = gqlCalls ? (serverPagination?.isLoading ?? false) : internalLoading;

  const calls = allCalls;
  const organization = useOrganizationStore((state) => state.organization);
  const [selectedCall, setSelectedCall] = useState<any>(null);
  const [editOutcomeCallId, setEditOutcomeCallId] = useState<string | null>(null);

  const getFlowInfo = (call_id?: string) => {
    if (!selectedCall?.flowExecution) {
      return { name: '-', type: '-' };
    }

    return {
      name: selectedCall.flowExecution.flow_name || '-',
      type: selectedCall.flowExecution.flow_type || '-',
    };
  };

  const columns: Column[] = [
    {
      header: 'Phone',
      key: 'to_number',
      width: '200px',
      isPinned: true,
      pinPosition: 'left',
      cell: (item) => (
        <div className="flex items-center gap-3" data-row-id={item.id}>
          {item.inbound ? (
            <PhoneIncoming className="h-4 w-4 call-color-inbound shrink-0" />
          ) : (
            <PhoneOutgoing className="h-4 w-4 call-color-outbound shrink-0" />
          )}
          <span className="truncate">{formatPhoneNumber(item.to_number)}</span>
          {(item.isTest || item.original_request?.test) && (
            <Badge className="inline-flex items-center badge-amber">Test</Badge>
          )}
        </div>
      ),
    },
    {
      header: 'Date/Time',
      key: 'callTimestamp',
      width: '220px',
      sortable: true,
      cell: (item) =>
        item.callTimestamp ? (
          <span className="whitespace-nowrap">
            {formatDate(item.callTimestamp, organization?.timezone)}
          </span>
        ) : (
          '-'
        ),
    },
    {
      header: 'Duration',
      key: 'call_length',
      width: '120px',
      sortable: true,
      cell: (item) => {
        const secs = item.duration || (item.call_length ? item.call_length * 60 : 0);
        if (!secs || secs <= 0) return '-';
        return formatCallDuration(secs);
      },
    },
    {
      header: 'Answered By',
      key: 'answered_by',
      width: '150px',
      sortable: true,
      cell: (item) => {
        const answeredBy = item.answered_by || 'no answer';
        const style = getAnsweredByStyle(answeredBy);

        if (!style) return null;

        return (
          <Badge className={`inline-flex items-center gap-1.5 ${style.background} ${style.text}`}>
            {answeredBy === 'human' ? (
              <>
                <User className="w-3.5 h-3.5" />
                <span>Human</span>
              </>
            ) : answeredBy === 'voicemail' ? (
              <>
                <Voicemail className="w-3.5 h-3.5" />
                <span>Voicemail</span>
              </>
            ) : (
              <>
                <XCircle className="w-3.5 h-3.5" />
                <span>No Answer</span>
              </>
            )}
          </Badge>
        );
      },
    },
    {
      header: 'Flow',
      key: 'flow_name',
      width: '180px',
      cell: (item) => item.flowExecution?.flow_name || '-',
    },
    {
      header: 'Flow Type',
      key: 'flow_type',
      width: '120px',
      cell: (item) => {
        const type = item.flowExecution?.flow_type || '';
        if (!type) return <span className="text-muted-foreground">-</span>;

        const style = getFlowTypeStyle(type);
        return (
          <Badge className={`inline-flex items-center gap-1.5 ${style.background} ${style.text}`}>
            <span>{type}</span>
          </Badge>
        );
      },
    },
    {
      header: 'Outcome',
      key: 'call_analysis',
      width: '150px',
      cell: (item) => {
        const outcome = item.call_analysis?.outcome;
        if (!outcome) return <span className="text-muted-foreground">-</span>;
        return <OutcomeBadge outcome={outcome} />;
      },
    },
  ];

  if (queryError) {
    return <div className="text-red-500 p-4">Error loading calls: {queryError.message}</div>;
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Table is always rendered; clicking a row opens the call details in
          a modal instead of taking over the page. */}
      <div
        className="flex flex-col flex-1 min-h-0"
        onClick={(e) => {
          if (loading) return;
          const row = (e.target as HTMLElement).closest('tr');
          if (!row || row.closest('thead')) return;
          // Look up the call by id stored on the row's pinned cell —
          // robust against duplicate callIds, sort order, and React
          // reconciliation reordering rows.
          const idEl = row.querySelector('[data-row-id]') as HTMLElement | null;
          const id = idEl?.getAttribute('data-row-id');
          if (!id) return;
          const call = calls.find((c) => c.id === id);
          if (call) setSelectedCall(call);
        }}
      >
        <TableCard
          borderless
          className="flex-1 min-h-0 cursor-pointer"
          footer={
            serverPagination && serverPagination.total !== undefined ? (
              serverPagination.total > 0 ? (
                <OffsetPagination
                  currentPage={serverPagination.currentPage ?? 0}
                  totalItems={serverPagination.total}
                  pageSize={serverPagination.pageSize ?? 20}
                  onNextPage={serverPagination.onNextPage}
                  onPrevPage={serverPagination.onPrevPage ?? (() => {})}
                  onPageChange={serverPagination.onPageChange}
                  isLoading={serverPagination.isLoading}
                  label="calls"
                />
              ) : null
            ) : (
              <TablePagination
                hasNext={serverPagination ? serverPagination.hasNext : internalHasNext}
                onNextPage={serverPagination ? serverPagination.onNextPage : internalHandleNextPage}
                isLoading={serverPagination ? serverPagination.isLoading : loading}
              />
            )
          }
        >
          <Table
            data={calls}
            columns={columns}
            loading={loading}
            keyExtractor={(item) => item.id}
            emptyStateText="No call history available"
            size="md"
            sortable={false}
          />
        </TableCard>
      </div>

      {/* Call details modal — replaces the previous in-place takeover. */}
      <Modal isOpen={!!selectedCall} onClose={() => setSelectedCall(null)} size="xl">
        <Modal.Header>
          <Typography variant="h3">Call details</Typography>
        </Modal.Header>
        <Modal.Body className="overflow-hidden">
          <div className="max-h-[calc(100vh-200px)] overflow-y-auto scrollbar-custom">
            {selectedCall && (
              <CallDetailsPanel
                call={selectedCall}
                orgTimezone={organization?.timezone}
                flowInfo={getFlowInfo(selectedCall.call_id)}
                contactFirstName={contactFirstName || ''}
                assistantName={organization?.assistant_name}
                onEditOutcome={() => setEditOutcomeCallId(selectedCall.call_id)}
              />
            )}
          </div>
        </Modal.Body>
      </Modal>

      {editOutcomeCallId && (
        <EditCallAnalysisModal
          isOpen={!!editOutcomeCallId}
          onClose={() => setEditOutcomeCallId(null)}
          callId={editOutcomeCallId}
          currentOutcome={selectedCall?.call_analysis?.outcome || ''}
          onSuccess={(newOutcome) => {
            if (selectedCall) {
              setSelectedCall({
                ...selectedCall,
                call_analysis: { ...selectedCall.call_analysis, outcome: newOutcome },
              });
            }
          }}
        />
      )}
    </div>
  );
};
